import {
  CopyOutlined,
  FileExcelOutlined,
  LoadingOutlined,
  ReloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Input,
  notification,
  Row,
  Select,
  Skeleton,
  Table,
  Upload,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useContext, useEffect, useState } from "react";
import { read as XLSXread, utils as XLSXutils } from "xlsx";
import { ContentBox } from "../../../components/layout/content";
import { LoginedLayout } from "../../../components/layout/loginLayout";
import { HeaderTitle } from "../../../components/styled-components";
import { STATUS } from "../../../constants/option";
import { UserContext } from "../../../context/userContext";
import { exportOrderToExcel } from "../../../functions/exportExcel";
import { FormatDateYMDHMS } from "../../../functions/function";
import { OrderLists } from "../../../res/type";
import { PATCH, POST } from "../../../services";
import {
  CHECKER_ORDERS_FILTER,
  CHECKER_UPLOAD_INVOICE,
  CHECKER_UPLOAD_TRACKING,
} from "../../../services/path";
import EditModal from "./view/editModal";
import PreviewModal from "./view/preview_pdf";

const SearchOrder = () => {
  const [orderNo, setOrderNo] = useState<string | null>(null);
  const [filePath, setFilePath] = useState<string>("");
  const [data, setData] = useState<OrderLists[]>([]);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const hasLocalStorageDate = localStorage.getItem("date") !== null;
  const [startDateRange, setStartDateRange] = useState<string>("");
  const [endDateRange, setEndDateRange] = useState<string>("");
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    hasLocalStorageDate ? null : dayjs(),
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(hasLocalStorageDate ? null : dayjs());
  const [isExpressDelivery, setIsExpressDelivery] = useState<boolean | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalPreview, setIsModalPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingTracking, setIsUploadingTracking] = useState(false);
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<OrderLists | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const { user } = useContext(UserContext);
  const [searchParams, setSearchParams] = useState({
    searchTextWord: "",
    searchTextOrderNo: "",
    searchTextTracking: "",
    searchTextCategory: "",
    searchTextPlatform: "",
    searchTextPlatformStatus: "",
    searchTextLogistic: "",
    searchTextBrand: "",
    searchTextStatus: "",
    searchTextBatch: "",
    searchTextDate: "",
    searchTextInvoice: "",
    searchTextSo: "",
    searchTextTotalPrice: "",
  });

  useEffect(() => {
    fetchDefaultLocalStorage();
  }, []);

  useEffect(() => {
    fetchData();
  }, [isExpressDelivery, startDateRange, endDateRange]);

  const checkMatch = (fieldValue: string, searchValue: string) => {
    if (!searchValue) return true;
    const searchValues = searchValue.split(",,, ").map((val: string) => val.trim());
    return searchValues.some((val) => {
      return fieldValue.toLowerCase() === val.toLowerCase();
    });
  };

  const fetchDefaultLocalStorage = () => {
    const date = localStorage.getItem("date");
    const operationStatus = localStorage.getItem("operationStatus") || "";
    const platform = localStorage.getItem("platform") || "";
    const logistic = localStorage.getItem("logistic") || "";
    const brand = localStorage.getItem("brand") || "";
    const isExpressDelivery = localStorage.getItem("isExpressDelivery") === "true";

    if (date && (operationStatus || platform || logistic || brand)) {
      if (operationStatus) {
        setSelectedStatus([operationStatus]);
      }

      setIsExpressDelivery(isExpressDelivery);
      handleSearchInputChange("searchTextLogistic", logistic);
      handleSearchInputChange("searchTextPlatform", platform);
      handleSearchInputChange("searchTextPlatBrand", brand);
      setSearchParams((prevSearchParams) => ({
        ...prevSearchParams,
        searchTextStatus: operationStatus,
        searchTextPlatform: platform,
        searchTextLogistic: logistic,
        searchTextBrand: brand,
      }));

      const startDateValue = dayjs(date);
      const endDateValue = dayjs(date);
      setStartDate(startDateValue);
      setEndDate(endDateValue);
      setStartDateRange(date);
      setEndDateRange(date);
      localStorage.clear();
    }
  };

  const fetchData = async () => {
    if (!startDateRange || !endDateRange || isExpressDelivery == null) return;
    try {
      setIsLoading(true);
      const requestBody = {
        search: searchParams.searchTextWord,
        start_date: startDateRange,
        end_date: endDateRange,
        is_batch_time: true,
        is_express_delivery: isExpressDelivery,
      };

      const response = await POST(CHECKER_ORDERS_FILTER, requestBody);
      setData(response.result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResyncWaybill = () => {
    try {
      setIsReloading(true);
      fetchData();
      localStorage.clear();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsReloading(false);
    }
  };

  const handleUploadExcel = async (event: any) => {
    if (!event) return;
    const file = event;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = e.target?.result;
      const workbook = XLSXread(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawData = XLSXutils.sheet_to_json(worksheet, { header: 1 }) as any[][];
      const jsonData = rawData.slice(1).map((row) => ({
        order_no: row[0],
        tracking_no: row[1],
      }));

      try {
        setIsUploadingTracking(true);
        const response = await PATCH(CHECKER_UPLOAD_TRACKING, { data: jsonData });
        if (response) {
          notification.success({
            message: "Upload successful",
            duration: 0,
          });
        } else {
          notification.error({
            message: "Upload failed. Please try again.",
            duration: 0,
          });
        }
      } catch (error) {
        notification.error({
          message: "Upload failed. Please try again.",
          duration: 0,
        });
      } finally {
        setIsUploadingTracking(false);
      }
    };

    reader.readAsBinaryString(file);
    return false; // To prevent automatic upload
  };

  const handleFileUpload = async (event: any) => {
    const file = event;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = e.target?.result;
      const workbook = XLSXread(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawData = XLSXutils.sheet_to_json(worksheet, { header: 1 }) as any[][];
      const jsonData = rawData.slice(1).map((row: any[]) => {
        const taxBranchCode = row[2] === "Company" ? "00000" : (row[7] || "").toString();
        return {
          order_no: (row[0] || "").toString(),
          tax_invoice_type: (row[2] || "").toString(),
          tax_id: (row[3] || "").toString(),
          tax_branch_type: (row[4] || "").toString(),
          tax_name_customer: (row[5] || "").toString(),
          tax_branch_name: (row[6] || "").toString(),
          tax_branch_code: taxBranchCode,
          tax_address: (row[8] || "").toString(),
          tax_district: (row[9] || "").toString(),
          tax_province: (row[10] || "").toString(),
          tax_post_code: (row[11] || "").toString(),
          tax_email: (row[12] || "").toString(),
          tax_phone_num: (row[13] || "").toString(),
          is_req_inv: (row[1] || "").toLowerCase() === "yes",
        };
      });

      try {
        setIsUploadingInvoice(true);
        const response = await PATCH(CHECKER_UPLOAD_INVOICE, { invoice: jsonData });
        if (response) {
          notification.success({
            message: "Upload successful",
            duration: 0,
          });
        } else {
          notification.warning({
            message: "Upload failed. Please try again.",
            duration: 0,
          });
        }
      } catch (error) {
        notification.error({
          message: "Upload failed. Please try again.",
          duration: 0,
        });
      } finally {
        setIsUploadingInvoice(false);
      }
    };

    reader.readAsBinaryString(file);
    return false; // To prevent automatic upload
  };

  const handleExportToExcel = () => {
    if (filteredData.length === 0) {
      notification.error({
        message: "No data to export.",
        duration: 5,
      });

      return;
    }

    exportOrderToExcel(filteredData, "Order_Data");
    notification.success({
      message: "Data exported to Excel successfully.",
      duration: 5,
    });
  };

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: string[]) => {
    if (dates) {
      if (typeof dateStrings[0] === "string") {
        setSearchParams((prev) => ({ ...prev, searchTextWord: "" }));
        setStartDateRange(dateStrings[0]);
        setEndDateRange(dateStrings[1]);
        setStartDate(dates[0]);
        setEndDate(dates[1]);
      }
    }
  };

  const handleSearchInputChange = (field: string, value: string) => {
    setSearchParams({ ...searchParams, [field]: value });
    setCurrentPage(1);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleOpenModal = (record: OrderLists) => {
    setSelectedRow(record);
    setIsModalVisible(true);
  };

  const handlePreviewPdfModal = (record: OrderLists) => {
    setOrderNo(record.order_no);
    setFilePath(record.file_path ?? "");
    setIsModalPreview(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setIsModalPreview(false);
    setSelectedRow(null);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    return (
      checkMatch(item.order_no ?? "", searchParams.searchTextOrderNo) &&
      checkMatch(item.category ?? "", searchParams.searchTextCategory) &&
      checkMatch(item.platform ?? "", searchParams.searchTextPlatform) &&
      checkMatch(item.platform_status ?? "", searchParams.searchTextPlatformStatus) &&
      checkMatch(item.logistic ?? "", searchParams.searchTextLogistic) &&
      checkMatch(item.brand ?? "", searchParams.searchTextBrand) &&
      checkMatch(item.operation_status ?? "", searchParams.searchTextStatus) &&
      checkMatch(item.batch_create_date ?? "", searchParams.searchTextDate) &&
      checkMatch(item.batch_id ?? "", searchParams.searchTextBatch) &&
      checkMatch(item.tracking_no ?? "", searchParams.searchTextTracking) &&
      checkMatch(item.invoice_no ?? "", searchParams.searchTextInvoice) &&
      checkMatch(item.so_no ?? "", searchParams.searchTextSo) &&
      checkMatch(item.total_price ?? "", searchParams.searchTextTotalPrice)
    );
  });

  const pageOptions = ["10", "25", "50", "100"];
  const columns = [
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Order No:
          </span>
          <Input
            placeholder="Search Order No."
            value={searchParams.searchTextOrderNo}
            onChange={(e) => handleSearchInputChange("searchTextOrderNo", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "order_no",
      key: "order_no",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.order_no ?? "";
        const B = b.order_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Tracking No:
          </span>
          <Input
            placeholder="Search Tracking No."
            value={searchParams.searchTextTracking}
            onChange={(e) => handleSearchInputChange("searchTextTracking", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "tracking_no",
      key: "tracking_no",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.tracking_no ?? "";
        const B = b.tracking_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Category:
          </span>
          <Input
            placeholder="Search Category."
            value={searchParams.searchTextCategory}
            onChange={(e) => handleSearchInputChange("searchTextCategory", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "category",
      key: "category",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.category ?? "";
        const B = b.category ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Platform:
          </span>
          <Input
            placeholder="Search PlatForm."
            value={searchParams.searchTextPlatform}
            onChange={(e) => handleSearchInputChange("searchTextPlatform", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "platform",
      key: "platform",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.platform ?? "";
        const B = b.platform ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "25px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "110px",
              maxWidth: "110px",
            }}
          >
            Platform Status:
          </span>
          <Input
            placeholder="Search Platform Status."
            value={searchParams.searchTextPlatformStatus}
            onChange={(e) => handleSearchInputChange("searchTextPlatformStatus", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "platform_status",
      key: "platform_status",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.platform_status ?? "";
        const B = b.platform_status ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Logistic:
          </span>
          <Input
            placeholder="Search Logistic."
            value={searchParams.searchTextLogistic}
            onChange={(e) => handleSearchInputChange("searchTextLogistic", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "logistic",
      key: "logistic",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.logistic ?? "";
        const B = b.logistic ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Store:
          </span>
          <Input
            placeholder="Search Store."
            value={searchParams.searchTextBrand}
            onChange={(e) => handleSearchInputChange("searchTextBrand", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "brand",
      key: "brand",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.brand ?? "";
        const B = b.brand ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Status:
          </span>
          <Select
            mode="tags"
            placeholder="Select or Input Status."
            value={selectedStatus}
            onChange={(values) => {
              setSelectedStatus(values);
              handleSearchInputChange("searchTextStatus", values.join(",,, "));
            }}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
          >
            {STATUS.map((item) => (
              <Select.Option key={item.value} value={item.label}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
      dataIndex: "operation_status",
      key: "operation_status",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.operation_status ?? "";
        const B = b.operation_status ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Batch No:
          </span>
          <Input
            placeholder="Search Batch ID"
            value={searchParams.searchTextBatch}
            onChange={(e) => handleSearchInputChange("searchTextBatch", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "batch_id",
      key: "batch_id",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.batch_id ?? "";
        const B = b.batch_id ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Date:
          </span>
          <Input
            placeholder="Search Date"
            value={searchParams.searchTextDate}
            onChange={(e) => handleSearchInputChange("searchTextDate", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "batch_create_date",
      key: "batch_create_date",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.batch_create_date ?? "";
        const B = b.batch_create_date ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>
            {FormatDateYMDHMS(text.replace("T", " ").replace("Z", ""))}
          </span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Invoice No:
          </span>
          <Input
            placeholder="Search Invoice No."
            value={searchParams.searchTextInvoice}
            onChange={(e) => handleSearchInputChange("searchTextInvoice", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "invoice_no",
      key: "invoice_no",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.invoice_no ?? "";
        const B = b.invoice_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            So No:
          </span>
          <Input
            placeholder="Search So No."
            value={searchParams.searchTextSo}
            onChange={(e) => handleSearchInputChange("searchTextSo", e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
            style={{ width: 200 }}
            suffix={<SearchOutlined style={{ color: "gray" }} />}
          />
        </div>
      ),
      dataIndex: "so_no",
      key: "so_no",
      sorter: (a: OrderLists, b: OrderLists) => {
        const A = a.so_no ?? "";
        const B = b.so_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (record: OrderLists) =>
        [1, 5, 6, 7].includes(user.role_id) ? (
          <Button type="primary" onClick={() => handlePreviewPdfModal(record)}>
            Preview
          </Button>
        ) : null,
    },
  ];
  return (
    <LoginedLayout>
      <ContentBox>
        <HeaderTitle>
          <h2>Orders</h2>
        </HeaderTitle>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ flex: 3, marginRight: "8px" }}>
            <span>Date Range:</span>
            <DatePicker.RangePicker
              className="small-date-picker"
              style={{ marginLeft: "8px" }}
              value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
              showTime={false} // Add this line to display a single calendar
              onChange={handleRangeChange}
            />
          </div>
          <div style={{ flex: 2 }}>
            <Input.Search
              placeholder="Search for Order No., Tracking No., Batch No."
              value={searchParams.searchTextWord}
              onChange={(e) => handleSearchInputChange("searchTextWord", e.target.value)}
              onSearch={fetchData}
            />
          </div>
        </div>
        <Row
          align="middle"
          justify="space-between"
          gutter={[8, 8]}
          style={{ marginBottom: "20px" }}
        >
          <Col flex={3}>
            <span>Show: </span>
            <Select
              value={pageSize.toString()}
              onChange={(value) => handlePageSizeChange(parseInt(value))}
            >
              {pageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            <Button
              type="primary"
              onClick={handleResyncWaybill}
              icon={isReloading ? null : <ReloadOutlined />}
              loading={isReloading}
              style={{
                backgroundColor: "transparent", // Make background transparent
                borderColor: "transparent", // Remove border
                color: "#767676", // Blue color for the icon
                boxShadow: "none", // Remove any shadow effects
              }}
            ></Button>
          </Col>
          {[1, 6, 7].includes(user.role_id) && (
            <Col>
              <Upload accept=".xlsx, .xls" beforeUpload={handleUploadExcel} showUploadList={false}>
                <Button
                  icon={isUploadingTracking ? <LoadingOutlined /> : <UploadOutlined />}
                  loading={isUploadingTracking}
                  style={{ backgroundColor: "#2196F3", borderColor: "#2196F3", color: "white" }} // Blue color
                >
                  {isUploadingTracking ? "Uploading..." : "Update Tracking"}
                </Button>
              </Upload>
            </Col>
          )}
          {[1, 5, 6, 7].includes(user.role_id) && (
            <Col>
              <Upload accept=".xlsx, .xls" beforeUpload={handleFileUpload} showUploadList={false}>
                <Button
                  icon={isUploadingInvoice ? <LoadingOutlined /> : <UploadOutlined />}
                  loading={isUploadingInvoice}
                  style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50", color: "white" }} // Original green
                >
                  {isUploadingInvoice ? "Uploading..." : "Upload invoice"}
                </Button>
              </Upload>
            </Col>
          )}
          {[1, 5, 6, 7].includes(user.role_id) && (
            <Col>
              <Button
                onClick={handleExportToExcel}
                type="primary"
                icon={<FileExcelOutlined />}
                style={{ backgroundColor: "#8BC34A", borderColor: "#8BC34A", color: "white" }} // Lighter green
              >
                Export Excel
              </Button>
            </Col>
          )}
        </Row>

        {isLoading ? (
          <Skeleton active />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            scroll={{ x: 1500 }} // Adjust the value based on your needs
            pagination={{
              position: ["bottomRight"],
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              onShowSizeChange: (current, size) => handlePageSizeChange(size),
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
              total: filteredData.length,
            }}
            onRow={(record) => ({
              onDoubleClick: () => handleOpenModal(record),
            })}
          />
        )}

        {selectedRow && (
          <EditModal
            visible={isModalVisible}
            onCancel={handleCloseModal}
            order_no={selectedRow?.order_no || ""}
            tracking_no={selectedRow?.tracking_no || ""}
            no={selectedRow?.no || 0}
          />
        )}
        {isModalPreview && orderNo && (
          <PreviewModal
            visible={isModalPreview}
            onCancel={handleCloseModal}
            order_no={orderNo} // Pass the selected order as a prop
            file_path={filePath}
          />
        )}
      </ContentBox>
    </LoginedLayout>
  );
};

export default SearchOrder;
